///
/// EarthBrowser
///
/// Created by Matt Giger on 10/29/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Numeric safe-area insets (status bar / Dynamic Island, home indicator,
/// landscape notch) for layout code that works in pixels rather than CSS.
/// CSS env() can't be read through getComputedStyle, so a hidden probe
/// element is sized with the four env() values and measured; re-measured
/// on resize/orientation change. Components that can use CSS should
/// prefer var(--safe-area-inset-*) directly (globals.css).
///

import { useSyncExternalStore } from "react"

export interface SafeAreaInsets {
	top: number
	right: number
	bottom: number
	left: number
}

const cZero: SafeAreaInsets = { top: 0, right: 0, bottom: 0, left: 0 }

let current: SafeAreaInsets = cZero
let probe: HTMLDivElement | null = null
const listeners = new Set<() => void>()

function ensureProbe(): HTMLDivElement | null {
	if (typeof document === "undefined") return null
	if (probe) return probe
	probe = document.createElement("div")
	probe.setAttribute("aria-hidden", "true")
	probe.style.cssText = [
		"position:fixed", "left:0", "top:0", "visibility:hidden", "pointer-events:none",
		"padding-top:env(safe-area-inset-top,0px)",
		"padding-right:env(safe-area-inset-right,0px)",
		"padding-bottom:env(safe-area-inset-bottom,0px)",
		"padding-left:env(safe-area-inset-left,0px)",
	].join(";")
	document.body.appendChild(probe)
	return probe
}

function measure(): void {
	const el = ensureProbe()
	if (!el) return
	const style = getComputedStyle(el)
	const next: SafeAreaInsets = {
		top: parseFloat(style.paddingTop) || 0,
		right: parseFloat(style.paddingRight) || 0,
		bottom: parseFloat(style.paddingBottom) || 0,
		left: parseFloat(style.paddingLeft) || 0,
	}
	if (next.top !== current.top || next.right !== current.right || next.bottom !== current.bottom || next.left !== current.left) {
		current = next
		listeners.forEach(fn => fn())
	}
}

function subscribe(onChange: () => void): () => void {
	if (listeners.size === 0) {
		measure()
		window.addEventListener("resize", measure)
		window.addEventListener("orientationchange", measure)
	}
	listeners.add(onChange)
	return () => {
		listeners.delete(onChange)
		if (listeners.size === 0) {
			window.removeEventListener("resize", measure)
			window.removeEventListener("orientationchange", measure)
		}
	}
}

/** Current safe-area insets in CSS pixels; all zero on desktop browsers */
export function useSafeAreaInsets(): SafeAreaInsets {
	return useSyncExternalStore(subscribe, () => current, () => cZero)
}
